<template>
    <div>
        <hts-slide-over
            :show="props.show"
            :title="$t('labels.supplyDocument.title')"
            width="50%"
            @close="closeOverSide">
            <template #body>
                <hts-card>
                    <template #toolbar>
                        <div class="w-full text-right">
                            <hts-form-button
                                v-if="group.isAdmin || permissions.includes('supply_document_add')"
                                type="submit"
                                icon-name="hi-plus-circle-icon"
                                icon-position="right"
                                @click="onCreateSupplyDocument">
                                {{ $t('labels.add') }}
                            </hts-form-button>
                        </div>
                    </template>
                    <div>
                        <hts-data-table
                            ref="tableSupplyDocument"
                            :headers="supplyPluginHeader"
                            :server="getApi"
                            :get-context-menu-items="getContextMenuItems" />
                    </div>
                </hts-card>
            </template>
            <template #footer>
                <div />
            </template>
        </hts-slide-over>
        <supply-document-editor
            v-if="showDocumentEditor"
            :show="showDocumentEditor"
            :action="supplyDocument"
            :document-data="documentSelected"
            @close="showDocumentEditor = false"
            @on-submit="onSubmit" />
        <hts-modal
            :show="showModalDelete"
            :title="$t('labels.deleteSupplyDocument')"
            type="confirm"
            :content="$t('labels.confirmDeleteSupplyDocument')"
            @hide="onCloseModal"
            @confirm="() => onDelete(idsDelete)" />
    </div>
</template>

<script lang="ts" setup>
import { ESupplyDocumentPrivacy, ESupplyDocumentType, ISupplyDocument } from '../models/supply'
import { onHanlderType } from '../service/helper'
import SupplyDocumentEditor from './supply-document-editor.vue'
import lodash from '~/composables/lodash'

const { $formatters } = useNuxtApp()
const store = $store()
const group = store.system.getGroup
const permissions = ref<Array<String>>([])
onMounted(() => {
  permissions.value = group.permissions
})
const emits = defineEmits(['close'])
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
    required: true
  },
  id: {
    type: String,
    default: '',
    required: true
  }
})

const showModalDelete = ref(false)
const idsDelete = ref<string[]>([])

const onConfirmDelete = (ids: string[]) => {
  showModalDelete.value = true
  idsDelete.value = ids
}

const onCloseModal = () => {
  showModalDelete.value = false
}

const supplyPluginHeader = [
  {
    field: 'fileName',
    headerName: $t('labels.documentName'),
    sortable: true
  },
  {
    field: 'type',
    headerName: $t('labels.supplyDocument.type'),
    valueGetter: (params: any) => {
      return onHanlderType(params.data.type)
    },
    sortable: true
  },
  {
    field: 'privacy',
    headerName: $t('labels.supplyDocument.view'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.privacy === 0) {
        return $t('labels.driverDocument.public')
      }
      if (params.data.privacy === 1) {
        return $t('labels.driverDocument.privacy')
      }
    }
  },
  {
    field: 'fileExtension',
    headerName: $t('labels.documentExtension')
  },
  {
    field: 'fileSize',
    headerName: $t('labels.documentSize'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.fileSize) {
        if (params.data.fileSize < 1000000) {
          return (params.data.fileSize / 1000).toFixed(1).toString() + ' KB'
        }
        return (params.data.fileSize / 1000000).toFixed(1).toString() + ' MB'
      }
    }
  },
  {
    field: 'path',
    headerName: $t('labels.supplyDocument.url'),
    sortable: true,
    valueFormatter: (params: any) => {
      return $formatters.cdnUrl(params.data)
    },
    onCellClicked: (params: any) => {
      window.open($formatters.cdnUrl(params.data), '_blank')
    }
  },
  {
    field: 'description',
    headerName: $t('labels.supplyDocument.description'),
    sortable: true
  },
  {
    field: 'createdAt',
    headerName: $t('labels.documentCreateDate'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  }
]

const getContextMenuItems = (params: any) => {
  if (params.node && params.node.data) {
    const menuItem = [
      group.isAdmin || permissions.value.includes('supply_document_edit')
        ? {
          name: $t('labels.supplyDocument.edit'),
          action: () => {
            documentSelected.ID = params.node.data.id
            documentSelected.title = params.node.data.fileName
            documentSelected.supply = params.node.data.supply
            documentSelected.type = params.node.data.type
            documentSelected.url = params.node.data.url
            documentSelected.description = params.node.data.description
            supplyDocument.value = 'edit'
            showDocumentEditor.value = true
          }
        }
        : null,
      group.isAdmin || permissions.value.includes('supply_document_delete')
        ? {
          name: $t('labels.supplyDocument.delete'),
          action: () => {
            const ids = [params.node.data.id]
            onConfirmDelete(ids)
          }
        }
        : null
    ]
    return menuItem
  }
  return []
}

const getId = computed(() => {
  return props.id
})

const supplyDocumentDefault: ISupplyDocument = {
  ID: '',
  title: '',
  supply: getId.value,
  type: ESupplyDocumentType.FUEL,
  privacy: ESupplyDocumentPrivacy.PRIVACY,
  description: '',
  url: ''
}

const documentSelected: ISupplyDocument = reactive({ ...supplyDocumentDefault })

const showDocumentEditor = ref(false)
const supplyDocument = ref('create')
const tableSupplyDocument = ref()

const getApi = computed(() => {
  const url = $url.supply.document.supplyDocumentListById
  const temp = Object.assign({}, url)
  temp.url = temp.url.replace('ID', props.id)
  return temp
})

const closeOverSide = () => {
  emits('close')
}
const onCreateSupplyDocument = () => {
  supplyDocument.value = 'create'
  Object.assign(documentSelected, { ...supplyDocumentDefault })
  showDocumentEditor.value = true
}

const onSubmit = lodash.debounce(async (data: any, action: string) => {
  const url = action === 'create' ? $url.supply.document.supplyDocumentCreate : $url.supply.document.supplyDocumentUpdate
  const formData = new FormData()
  formData.append('supply', data.supply)
  formData.append('title', data.title)
  formData.append('type', data.type)
  formData.append('description', data.description)
  formData.append('privacy', data.privacy)
  for (let i = 0; i < data.document.length; i++) {
    formData.append('document', data.document[i])
  }

  if (action === 'edit') {
    formData.append('ID', data.ID)
  }
  const result = await $api(url, { body: formData })
  const success = $lodash.get(result, 'data.value.success')
  const error = $lodash.get(result, 'data.value.data')
  if (success) {
    if (action === 'create') {
      $toast().success($t('labels.createSussess'))
    }
    if (action === 'edit') {
      $toast().success($t('labels.updateSuccess'))
    }
    tableSupplyDocument.value.refresh()
    showDocumentEditor.value = false
  } else {
    process.client && $toast().error(error.errors[0].msg)
  }
}, 500)

const onDelete = $lodash.debounce(async (ids: String[]) => {
  const url = $url.supply.document.supplyDocumentDelete
  const result = await $api(url, { body: { ids } })
  const success = $lodash.get(result, 'data.value.success')
  const error = $lodash.get(result, 'data.value.data')
  if (success) {
    $toast().success($t('labels.deleteSuccess'))
    tableSupplyDocument.value.refresh()
    onCloseModal()
  } else {
    $toast().error(error)
  }
})
</script>
